import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Autocomplete, OutlinedInput } from "~/components/UI";
import { useField, FormikHandlers } from "formik";
import _ from "lodash";
import api from "~/api";

interface FromikSelectInboundVirtualTerminalProps {
  name: string;
  defaultInputValue?: string;
  filterOptions?: (
    options: InboundVirtualTerminalsApi.InboundVirtualTerminalDto[],
    state: any
  ) => InboundVirtualTerminalsApi.InboundVirtualTerminalDto[];
}

interface SelectInboundVirtualTerminalProps {
  inputValue?: string;
  name?: string;
  onChange?: (
    inboundVirtualTerminal?: InboundVirtualTerminalsApi.InboundVirtualTerminalDto
  ) => void;
  onChangeValue?: (value?: string) => void;
  onBlur?: FormikHandlers["handleBlur"];
  error?: string | undefined;
  touched?: boolean;
  autoClear?: boolean;
  filterOptions?: (
    options: InboundVirtualTerminalsApi.InboundVirtualTerminalDto[],
    state: any
  ) => InboundVirtualTerminalsApi.InboundVirtualTerminalDto[];
}

const requestSearchInboundVirtualTerminal = (search: string) =>
  api.inboundVirtualTerminals.getAll(20, 1, { search });

export const SelectInboundVirtualTerminal = React.memo(
  ({
    name = "",
    onChange,
    onChangeValue,
    onBlur = () => {},
    touched,
    error,
    filterOptions,
    inputValue,
  }: SelectInboundVirtualTerminalProps) => {
    const [value, setValue] = useState<string | undefined>(inputValue || "");
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<
      InboundVirtualTerminalsApi.InboundVirtualTerminalDto[]
    >([]);
    const [inboundVirtualTerminal, setInboundVirtualTerminal] =
      useState<Partial<InboundVirtualTerminalsApi.InboundVirtualTerminalDto> | null>(
        null
      );

    const searchInboundVirtualTerminals = useMemo(
      () =>
        _.debounce((inboundVirtualTerminalId: string) => {
          setLoading(true);
          requestSearchInboundVirtualTerminal(inboundVirtualTerminalId)
            .then(({ data }) => {
              setSearchResults(data.data);
            })
            .finally(() => {
              setLoading(false);
            });
        }, 500),
      []
    );

    const onChangeInputValue = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      setValue(e.target.value);
      onChangeValue && onChangeValue(e.target.value);
      searchInboundVirtualTerminals(e.target.value || "");
    };

    const onChangeAutocomplete = useCallback(
      (_e: React.ChangeEvent<{}>, newValue: any) => {
        if (newValue) {
          setValue(newValue.name);
          setInboundVirtualTerminal(newValue);
        } else {
          setValue("");
          setInboundVirtualTerminal(null);
        }
        onChange && onChange(newValue);
      },
      [onChange]
    );

    const getOptionSelected = useCallback(
      (
        o: InboundVirtualTerminalsApi.InboundVirtualTerminalDto,
        value: InboundVirtualTerminalsApi.InboundVirtualTerminalDto
      ) => o.id === value.id,
      []
    );

    const getOptionLabel = useCallback(
      (o: InboundVirtualTerminalsApi.InboundVirtualTerminalDto) => o.id || "",
      []
    );

    useEffect(() => {
      if (name && !inputValue) {
        setValue("");
        setInboundVirtualTerminal(null);
        searchInboundVirtualTerminals("");
      }
    }, [name, inputValue]);

    useEffect(() => {
      searchInboundVirtualTerminals("");
    }, [searchInboundVirtualTerminals]);

    return (
      <Autocomplete
        value={inboundVirtualTerminal}
        onChange={onChangeAutocomplete}
        getOptionSelected={getOptionSelected}
        onBlur={onBlur}
        noOptionsText="No InboundVirtualTerminals"
        options={searchResults}
        getOptionLabel={getOptionLabel}
        filterOptions={filterOptions}
        filterSelectedOptions={true}
        loading={loading}
        disableClearable
        renderInput={(params: any) => (
          <OutlinedInput
            className={params.InputProps.className}
            endAdornment={params.InputProps.endAdornment}
            inputProps={_.omit(params.inputProps, "value")}
            value={params.inputProps.value || value}
            touched={touched}
            error={error}
            fullWidth
            onChange={onChangeInputValue}
          />
        )}
      />
    );
  }
);

const FromikContainer = ({
  name,
  defaultInputValue,
  ...other
}: FromikSelectInboundVirtualTerminalProps &
  SelectInboundVirtualTerminalProps) => {
  const [field, meta] = useField(name);
  const { error, touched } = meta;

  const onChange = useCallback(
    (
      inboundVirtualTerminal?: InboundVirtualTerminalsApi.InboundVirtualTerminalDto
    ) => {
      field.onChange({
        target: {
          name,
          value: inboundVirtualTerminal ? inboundVirtualTerminal.id : "",
        },
      });
    },
    [field, name]
  );

  return (
    <SelectInboundVirtualTerminal
      {...field}
      {...other}
      onChange={onChange}
      touched={touched}
      error={error}
      inputValue={defaultInputValue || field.value}
    />
  );
};

export default React.memo(FromikContainer);
